/**
 * Apple owns the decision on whether the Apple Pay button may be shown — we no
 * longer sniff for Safari.
 *
 * With the Apple Pay JS SDK loaded, ApplePaySession is also defined outside
 * Safari: on desktop browsers such as Chrome/Edge/Firefox on Windows,
 * canMakePayments() reports true and tapping the button opens Apple's QR code,
 * which the customer confirms on an iPhone. Where Apple offers no experience at
 * all (Android web, for example) it reports false and Google Pay is left as the
 * only wallet button.
 *
 **/
export function isApplePayAvailable() {
    try {
        return !!(
            window.ApplePaySession &&
            typeof window.ApplePaySession.supportsVersion === "function" &&
            window.ApplePaySession.supportsVersion(3) &&
            window.ApplePaySession.canMakePayments()
        );
    } catch (error) {
        // Apple throws (a bare string) when the document is not served over https.
        return false;
    }
}

const SDK_SCRIPT_SELECTOR = 'script[src*="apple-pay-sdk.js"]';

/**
 * Last-resort backstop for the case where the SDK tag settled before this bundle
 * ran, so neither load nor error can still fire. Deliberately generous: it must
 * never turn a slow connection into "Apple Pay unavailable". The callback
 * re-checks availability, so an SDK that lands first wins.
 *
 **/
const SETTLE_BACKSTOP_MS = 20000;

/**
 * Settles once the SDK's fate is actually known, driven by the script tag's own
 * load/error events rather than a wall-clock deadline — a slow connection must
 * never be mistaken for "Apple Pay unavailable".
 *
 **/
export function onApplePaySdkSettled(callback) {
    if (window.ApplePaySession) {
        callback();

        return;
    }

    /**
     * The views load the SDK as a parser-blocking script and record the outcome
     * immediately after it, so by the time this runs the flag is authoritative:
     * `false` means the SDK definitively failed, not that it is still in flight.
     * Without it an already-fired error event would be unobservable and the
     * button would sit there dead.
     *
     **/
    if (window.blinkApplePaySdkLoaded === false) {
        callback();

        return;
    }

    const sdkScript = document.querySelector(SDK_SCRIPT_SELECTOR);

    if (sdkScript) {
        sdkScript.addEventListener("load", callback, { once: true });
        sdkScript.addEventListener("error", callback, { once: true });
        window.setTimeout(callback, SETTLE_BACKSTOP_MS);

        return;
    }

    /**
     * The tag can land after this bundle runs, so watch for it and then hand over
     * to its own load/error events. Expiring this watch settles nothing.
     *
     **/
    let attemptsLeft = 50;
    const poll = window.setInterval(() => {
        if (window.ApplePaySession) {
            window.clearInterval(poll);
            callback();

            return;
        }

        if (document.querySelector(SDK_SCRIPT_SELECTOR)) {
            window.clearInterval(poll);
            onApplePaySdkSettled(callback);

            return;
        }

        if (--attemptsLeft <= 0) {
            window.clearInterval(poll);
        }
    }, 100);
}
